import { createContext, PropsWithChildren, useEffect, useMemo, useState } from 'react';

import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useQuery } from '@tanstack/react-query';
import { getUrlParams, processApiPath } from '../utils/url';
import { GET_CLASS_DATA_PATH, GET_STUDENT_FOR_CODE, STUDENT } from '../config/apiPaths';
import { ClassSessionInfo, ClassStudent, Roles } from './types';
import { useUserUuid } from '../hooks/useUserUuid';
import { ClassData, SchoolSettings } from '../types/apiTypes';
import { filterSessionsByGroupAccess } from '../components/classroom/util';
import { getUserUuidHeaders } from '../utils/httpUtils';
import Loadable from '../components/common/loadable';
import { useAdminSettings } from './api';

interface ClassContextProps {
    classUuid: string;
}

interface ClassInfoContextInterface {
    isLoading: boolean;
    classData: ClassData; // raw data from GET_CLASS_DATA_PATH API call
    fetchClassInfo: () => void;
    errorMessage: string;
    classUuid: string;
    userUuid: string;
    me: Partial<ClassStudent>;
    meFetched: boolean;
    sessions: ClassSessionInfo[];
    adminSettings: SchoolSettings | null;
    previousSessions: ClassSessionInfo[] | undefined;
    upcomingSessions: ClassSessionInfo[] | undefined;
    setSessions: (sessions: ClassSessionInfo[]) => void;
}

const sessionDateComparator = (a, b) => {
    const aDate = new Date(Number(a.start_time));
    const bDate = new Date(Number(b.start_time));
    if (aDate < bDate) return -1;
    if (aDate > bDate) return 1;
    return 0;
};

const ClassContext = createContext({} as ClassInfoContextInterface);

const { Provider, Consumer } = ClassContext;

const ClassProvider = (props: PropsWithChildren<ClassContextProps>) => {
    const { classUuid, children } = props;

    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [sessions, setSessions] = useState<ClassSessionInfo[]>([]);
    const [previousSessionsRaw, setPreviousSessionsRaw] = useState<ClassSessionInfo[] | undefined>(undefined);
    const [upcomingSessionsRaw, setUpcomingSessionsRaw] = useState<ClassSessionInfo[] | undefined>(undefined);
    const [previousSessions, setPreviousSessions] = useState<ClassSessionInfo[] | undefined>(undefined);
    const [upcomingSessions, setUpcomingSessions] = useState<ClassSessionInfo[] | undefined>(undefined);

    const { t } = useTranslation('error_messages');
    const userUuid = useUserUuid(null) || '';
    const { code, is_guest: isGuest } = getUrlParams();

    const { data: adminSettings } = useAdminSettings();

    const {
        data: me,
        isFetched: meFetched,
        isError: meError
    } = useQuery({
        queryKey: [STUDENT, userUuid],
        enabled: !!userUuid && !!classUuid,
        queryFn: async () => {
            if (me?.student_id) {
                return me;
            }
            if (!userUuid || !classUuid) {
                return null;
            }
            if (isGuest) {
                return null;
            }

            /**
             * Fetches user details based on user_uuid token.
             */
            if (!code || code === 'undefined') {
                const response = await axios.get<Partial<ClassStudent>>(
                    processApiPath(STUDENT, undefined, {
                        class_uuid: classUuid
                    }),
                    { headers: getUserUuidHeaders(userUuid) }
                );

                return response.data;
            }

            /**
             * Exchanges an LTI code for user authentication details.
             * The user_uuid is associated with a user after exch.
             */
            if (code && code !== 'undefined') {
                const response = await axios.get<{ student: Partial<ClassStudent> }>(
                    processApiPath(GET_STUDENT_FOR_CODE, undefined, {
                        verification_code: code,
                        class_uuid: classUuid
                    }),
                    { headers: getUserUuidHeaders(userUuid) }
                );

                return response.data.student;
            }

            return undefined;
        }
    });

    const {
        data: classData,
        refetch: fetchClassData,
        isLoading,
        isError: classDataError
    } = useQuery({
        queryKey: [GET_CLASS_DATA_PATH, classUuid],
        enabled: !!userUuid && !!me?.student_id,
        queryFn: async () => {
            if (!userUuid || !me?.student_id) {
                return null;
            }

            const { data: classDataResponse } = await axios.post<ClassData>(
                processApiPath(GET_CLASS_DATA_PATH),
                {
                    class_uuid: classUuid,
                    with_info: 'true'
                },
                { headers: getUserUuidHeaders(userUuid) }
            );

            if (classDataResponse && classDataResponse.classSessions && classDataResponse.classSessionGroups) {
                const classSessionGroupsByUuid = {};

                classDataResponse.classSessionGroups.forEach((classSessionGroup) => {
                    classSessionGroupsByUuid[classSessionGroup.class_session_group_uuid] = classSessionGroup;
                });

                classDataResponse.classSessions.forEach((classSession) => {
                    if (classSession.class_session_group_uuid) {
                        classSession.class_session_group =
                            classSessionGroupsByUuid[classSession.class_session_group_uuid];
                    }
                });
            }

            return classDataResponse;
        }
    });

    useEffect(() => {
        if (!classData) {
            return;
        }

        const sortedSessions = classData.classSessions.sort(sessionDateComparator);

        const now = Date.now();
        const previous = sortedSessions.filter((s) => new Date(Number(s.end_time) * 1000).getTime() < now);
        const upcoming = sortedSessions.filter((s) => new Date(Number(s.end_time) * 1000).getTime() >= now);

        setSessions(sortedSessions);
        setPreviousSessionsRaw(previous);
        setUpcomingSessionsRaw(upcoming);
    }, [classData]);

    useEffect(() => {
        if (!meError) {
            return;
        }

        const key = enqueueSnackbar(t('class:error-message'), { variant: 'error' });

        return () => {
            closeSnackbar(key);
        };
    }, [meError, enqueueSnackbar, closeSnackbar, t]);

    useEffect(() => {
        if (!adminSettings || !previousSessionsRaw || !upcomingSessionsRaw || !me || !classData) {
            return;
        }

        const privilegedUser = me.role === Roles.Teacher || me.role === Roles.Assistant;
        const restrictSessions = adminSettings?.restrict_sessions_recordings_by_groups;

        if (restrictSessions && !privilegedUser && classData.classGroups.length) {
            // Filter sessions where the current user is a member of group(s) attributed to the session.
            const previous = filterSessionsByGroupAccess(
                previousSessionsRaw,
                classData.classGroups,
                classData.userGroups,
                me
            );

            const upcoming = filterSessionsByGroupAccess(
                upcomingSessionsRaw,
                classData.classGroups,
                classData.userGroups,
                me
            );

            setPreviousSessions(previous);
            setUpcomingSessions(upcoming);
        } else {
            setPreviousSessions(previousSessionsRaw);
            setUpcomingSessions(upcomingSessionsRaw);
        }
    }, [adminSettings, classData, me, previousSessionsRaw, upcomingSessionsRaw]);

    return useMemo(() => {
        if (!classData || !adminSettings || !me) {
            return <Loadable isLoading fullScreen />;
        }

        const value = {
            isLoading,
            adminSettings,
            classData,
            fetchClassInfo: fetchClassData,
            errorMessage: classDataError ? t('class:error-message') : '',
            classUuid,
            userUuid,
            me,
            meFetched,
            sessions,
            setSessions,
            previousSessions,
            upcomingSessions
        } satisfies ClassInfoContextInterface;

        return <Provider value={value}>{children}</Provider>;
    }, [
        adminSettings,
        children,
        classData,
        classDataError,
        classUuid,
        fetchClassData,
        isLoading,
        me,
        meFetched,
        previousSessions,
        sessions,
        t,
        upcomingSessions,
        userUuid
    ]);
};

export { ClassContext, ClassProvider, Consumer as SessionConsumer };
